import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  businessOutline,
  documentTextOutline,
  peopleOutline,
  swapHorizontalOutline,
} from 'ionicons/icons';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useMasters,
  useTransportDocumentsAccessGate,
  useWarehouseEnabled,
} from '../../lib/data/queries.js';

interface ShipmentSummary {
  id: string;
  business_id: string;
  kind: 'ddt' | 'trasferimento';
  document_scope?: 'collective' | 'restaurant';
  status: 'draft' | 'issued' | 'void';
  archived_at?: string | null;
}

interface BusinessSummary {
  id: string;
  tenant_id: string;
  archived_at?: string | null;
}

const actionCard: React.CSSProperties = {
  width: '100%',
  minHeight: 112,
  border: '1px solid #dbeafe',
  borderRadius: 16,
  background: '#fff',
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  textAlign: 'left',
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 4px 12px rgba(15,23,42,.05)',
};

export function TransportDocumentsPage(): JSX.Element {
  const history = useHistory();
  const { profile } = useAuth();
  const access = useTransportDocumentsAccessGate();
  const warehouseEnabled = useWarehouseEnabled();
  const { data: shipments = [] } = useMasters<ShipmentSummary>('shipments');
  const { data: businesses = [] } = useMasters<BusinessSummary>('businesses');

  const currentShipments = useMemo(
    () => shipments.filter((shipment) => (
      shipment.business_id === profile?.active_business_id
      && shipment.document_scope === 'restaurant'
      && !shipment.archived_at
    )),
    [profile?.active_business_id, shipments],
  );
  const stats = useMemo(() => ({
    salesDrafts: currentShipments.filter((s) => s.kind === 'ddt' && s.status === 'draft').length,
    salesIssued: currentShipments.filter((s) => s.kind === 'ddt' && s.status === 'issued').length,
    transferDrafts: currentShipments.filter((s) => s.kind === 'trasferimento' && s.status === 'draft').length,
  }), [currentShipments]);
  const otherBusinessCount = businesses.filter((business) => (
    business.tenant_id === profile?.active_tenant_id
    && business.id !== profile?.active_business_id
    && !business.archived_at
  )).length;

  if (access.checking) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
            <IonSpinner name="crescent" aria-label="Verifica accesso" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!access.enabled) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
            <IonTitle>Documenti di trasporto</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 14, padding: 16, color: '#9a3412' }}>
            Modulo non attivo o accesso non verificabile. Chiedi al consulente di
            abilitarlo per questo ristorante.
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Documenti di trasporto</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <main style={{ padding: '16px 16px 40px', maxWidth: 760, margin: '0 auto' }}>
          <section style={{
            background: warehouseEnabled ? '#ecfdf5' : '#eff6ff',
            border: `1px solid ${warehouseEnabled ? '#a7f3d0' : '#bfdbfe'}`,
            color: warehouseEnabled ? '#065f46' : '#1e40af',
            borderRadius: 14,
            padding: '12px 14px',
            marginBottom: 16,
            fontSize: 13,
            lineHeight: 1.45,
          }}>
            <strong>{warehouseEnabled ? 'Magazzino collegato' : 'Tracciabilità senza giacenza'}</strong>
            <div style={{ marginTop: 3 }}>
              {warehouseEnabled
                ? 'Le bozze riservano i lotti; emissione e annullamento muovono la giacenza con operazioni atomiche.'
                : 'Il documento conserva lavorazione, lotto e filiera. Le quantità restano dichiarate e non vengono presentate come disponibilità di magazzino.'}
            </div>
          </section>

          <h2 style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 4px 10px' }}>
            Scegli il documento
          </h2>
          <div style={{ display: 'grid', gap: 12 }}>
            <button type="button" style={actionCard} onClick={() => history.push('/documents/transport/sales')}>
              <span style={{ width: 48, height: 48, borderRadius: 14, background: '#dbeafe', color: '#1d4ed8', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <IonIcon icon={documentTextOutline} style={{ fontSize: 26 }} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>DDT di vendita</span>
                <span style={{ display: 'block', fontSize: 13, color: '#64748b', marginTop: 3, lineHeight: 1.35 }}>
                  Seleziona le lavorazioni, indica cliente e quantità, collega facoltativamente una fattura.
                </span>
                <span style={{ display: 'block', fontSize: 12, color: '#2563eb', marginTop: 7, fontWeight: 700 }}>
                  {stats.salesDrafts} bozze · {stats.salesIssued} emessi
                </span>
              </span>
            </button>

            <button type="button" style={actionCard} onClick={() => history.push('/documents/transport/internal')}>
              <span style={{ width: 48, height: 48, borderRadius: 14, background: '#dcfce7', color: '#15803d', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <IonIcon icon={swapHorizontalOutline} style={{ fontSize: 26 }} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>Trasferimento interno</span>
                <span style={{ display: 'block', fontSize: 13, color: '#64748b', marginTop: 3, lineHeight: 1.35 }}>
                  Sposta lotti prodotti tra due sedi della stessa azienda con documento numerato.
                </span>
                <span style={{ display: 'block', fontSize: 12, color: otherBusinessCount > 0 ? '#15803d' : '#b45309', marginTop: 7, fontWeight: 700 }}>
                  {otherBusinessCount > 0 ? `${stats.transferDrafts} bozze · ${otherBusinessCount} sedi disponibili` : 'Serve una seconda sede'}
                </span>
              </span>
            </button>
          </div>

          <section style={{ background: '#fff', borderRadius: 16, padding: '14px 16px', marginTop: 16, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <IonIcon icon={peopleOutline} style={{ fontSize: 22, color: '#475569' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Clienti e luoghi di consegna</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Anagrafica usata nell'intestazione del DDT.</div>
              </div>
              <IonButton fill="outline" size="small" onClick={() => history.push('/documents/transport/recipients')}>
                <IonIcon icon={businessOutline} slot="start" />
                Gestisci
              </IonButton>
            </div>
          </section>
        </main>
      </IonContent>
    </IonPage>
  );
}
